// The rep's own day (Oct 2, 2026) — home dashboard. Read-only: what the TL/ASM
// confirmed for today (blocks, area, team knock, apps / claims / signed IA
// goals) with live "done" counts, today's team knock details, and a
// "Plan changed?" note that texts the rep's TL + ASM on the Team line.

import { useEffect, useState } from "react";
import { supabase } from "../../lib/supabase";
import {
  BLOCKS, CONFIRM_DOWS, type DailyCommitment, type DayActuals, type TeamKnock,
  dowOf, fetchDayActuals, fetchKnocks, fetchMyCommitment, fmtClock, sendPlanChange, todayET,
} from "../../lib/dailyCommitments";

const fmt = (n: number | null | undefined) => (n == null ? "—" : Number.isInteger(n) ? String(n) : n.toFixed(1));
const BLOCK_LABEL: Record<string, string> = { am: "AM", pm: "PM", eve: "EVE" };

/**
 * hideGoals (Oct 27, 2026): a canvasser's home shows his appointment goal
 * against appointments he SET (canvasser_my_pay) in its own card; the "done"
 * counts here come from crew credit, which follows the deal's owner.
 */
export default function MyDayCard({ meId, hideGoals = false, readOnly = false }: { meId: string; hideGoals?: boolean; readOnly?: boolean }) {
  const day = todayET();
  const [row, setRow] = useState<DailyCommitment | null>(null);
  const [knocks, setKnocks] = useState<TeamKnock[]>([]);
  const [done, setDone] = useState<DayActuals | null>(null);
  const [confirmer, setConfirmer] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [tick, setTick] = useState(0);
  const [changeOpen, setChangeOpen] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [r, ks, act] = await Promise.all([fetchMyCommitment(meId, day), fetchKnocks(day), fetchDayActuals(day, [meId])]);
        if (cancelled) return;
        setRow(r); setKnocks(ks); setDone(act[meId] ?? { apps: 0, claims: 0, ia: 0 });
        if (r?.confirmed_by) {
          const { data } = await supabase.from("team_roster").select("full_name").eq("id", r.confirmed_by).maybeSingle();
          if (!cancelled) setConfirmer((data?.full_name as string | undefined)?.split(" ")[0] ?? null);
        }
      } catch {
        // The card is a convenience — on a failed read it just stays hidden.
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [meId, day, tick]);

  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    const bump = () => { if (t) clearTimeout(t); t = setTimeout(() => setTick((n) => n + 1), 1500); };
    const ch = supabase.channel(`my-day-${meId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "daily_commitments", filter: `user_id=eq.${meId}` }, bump)
      .on("postgres_changes", { event: "*", schema: "public", table: "team_knocks", filter: `knock_date=eq.${day}` }, bump)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "crew_activity_log", filter: `user_id=eq.${meId}` }, bump)
      .subscribe();
    return () => { if (t) clearTimeout(t); void supabase.removeChannel(ch); };
  }, [meId, day]);

  const isConfirmDay = CONFIRM_DOWS.has(dowOf(day));
  if (!loaded || (!row && !isConfirmDay && knocks.length === 0)) return null;

  const myKnock = knocks.find((k) => k.id === row?.team_knock_id);

  async function send() {
    if (note.trim().length < 3) { setMsg("Say what changed."); return; }
    setSending(true); setMsg(null);
    try {
      await sendPlanChange(day, note.trim());
      setMsg("Sent to your TL/ASM."); setNote(""); setChangeOpen(false);
    } catch (e) { setMsg((e as Error).message); } finally { setSending(false); }
  }

  return (
    <section className="mb-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" aria-label="Your day">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Your day · today</div>
        {row ? (
          row.is_off
            ? <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600">Off today{row.off_reason ? ` · ${row.off_reason}` : ""}</span>
            : <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700">✓ Confirmed{confirmer ? ` by ${confirmer}` : ""}</span>
        ) : (
          <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Not confirmed yet</span>
        )}
      </div>

      {!row && <p className="mt-2 text-sm text-slate-600">Your TL or ASM will call to confirm your day before 10:00.</p>}

      {row && !row.is_off && (
        <div className="mt-2 space-y-2">
          <div className="flex flex-wrap items-center gap-1.5">
            {BLOCKS.map((b) => (
              <span key={b} className={`w-10 rounded text-center text-[11px] font-bold ${row.blocks[b] ? "bg-bardin-orange text-white" : "bg-slate-100 text-slate-400"}`}>{BLOCK_LABEL[b]}</span>
            ))}
            {row.area && <span className="text-xs text-slate-600">📍 {row.area}</span>}
          </div>
          {!hideGoals && <div className="grid grid-cols-3 gap-2">
            {([["Apps", row.goal_apps, done?.apps], ["Claims", row.goal_claims, done?.claims], ["Signed IA", row.goal_ia, done?.ia]] as const).map(([lbl, g, d]) => {
              const hit = g != null && g > 0 && (d ?? 0) >= g;
              return (
                <div key={lbl} className={`rounded-lg border p-2 text-center ${hit ? "border-emerald-200 bg-emerald-50" : "border-slate-200 bg-slate-50"}`}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">{lbl}</div>
                  <div className="text-lg font-black tabular-nums text-bardin-ink">{fmt(d ?? 0)}<span className="text-sm font-semibold text-slate-400"> / {fmt(g)}</span></div>
                </div>
              );
            })}
          </div>}
          {row.note && <div className="text-xs text-slate-500">Note: {row.note}</div>}
        </div>
      )}

      {knocks.length > 0 && (
        <div className="mt-3 space-y-1.5">
          {knocks.map((k) => {
            const mine = row?.knock_status === "in" && myKnock?.id === k.id;
            return (
              <div key={k.id} className={`rounded-lg px-3 py-2 text-xs ${mine ? "bg-orange-50 ring-1 ring-orange-200" : "bg-slate-50"}`}>
                <div className="font-bold text-bardin-ink">🚪 Team knock{knocks.length > 1 ? ` ${k.label}` : ""} · {fmtClock(k.starts_at)} – {fmtClock(k.ends_at)}{mine ? " · you're in" : ""}</div>
                <div className="text-slate-600">Meet: {k.meet_location}{k.area_label ? ` · ${k.area_label}` : ""}</div>
              </div>
            );
          })}
          {row?.knock_status === "out" && <div className="text-xs text-slate-500">You're out of today's knock{row.knock_out_reason ? `: ${row.knock_out_reason}` : ""}.</div>}
        </div>
      )}

      {/* readOnly: someone else looking at this rep's page — "Plan changed?"
          sends as the signed-in person, so it isn't offered. */}
      {!readOnly && <div className="mt-3">
        {changeOpen ? (
          <div className="flex flex-wrap items-end gap-2">
            <label className="min-w-[200px] flex-1 text-[11px] font-semibold text-slate-500">What changed today?
              <input id="plan-change-note" className="w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Adjuster moved to 5pm — joining the knock at 6" />
            </label>
            <button onClick={send} disabled={sending} className="rounded-md bg-slate-900 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50">{sending ? "Sending…" : "Send to my TL/ASM"}</button>
            <button onClick={() => setChangeOpen(false)} className="rounded-md px-2 py-1.5 text-xs font-semibold text-slate-500">Cancel</button>
          </div>
        ) : (
          <button onClick={() => { setChangeOpen(true); setMsg(null); }} className="rounded-md border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">📅 Plan changed?</button>
        )}
        {msg && <div className="mt-1 text-xs text-slate-600">{msg}</div>}
      </div>}
    </section>
  );
}
